import getAuthenticityToken from '../components/application/shared/getAuthenticityToken';

// --- Types ---

export class ApiRequestError extends Error {
  status: number;
  body: unknown;

  constructor({ status, message, body }: { status: number; message: string; body: unknown }) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.body = body;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }
}

export interface RequestOptions {
  skipContentType?: boolean;
  headers?: Record<string, string>;
  params?: Record<string, string>;
  signal?: AbortSignal;
}

/* eslint-disable no-unused-vars */
export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  delete<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
}
/* eslint-enable no-unused-vars */

// --- Internals ---

const buildUrl = (path: string, params?: Record<string, string>): string => {
  if (!params || Object.keys(params).length === 0) return path;
  const searchParams = new URLSearchParams(params);
  return `${path}?${searchParams.toString()}`;
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!text) return undefined as T;

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiRequestError({
      status: response.status,
      message: `Expected JSON response but received unparseable body (status ${response.status})`,
      body: text,
    });
  }
};

const handleErrorResponse = async (response: Response): Promise<never> => {
  if (response.status === 401) {
    window.location.reload();
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = await response.text().catch(() => null);
  }

  const message = (body && typeof body === 'object' && 'error' in body)
    ? String((body as { error: string }).error)
    : `Request failed: ${response.statusText}`;

  throw new ApiRequestError({ status: response.status, message, body });
};

interface CreateClientConfig {
  authenticityToken?: string;
  resolveToken: () => string;
  injectToken: boolean;
}

const buildClient = (config: CreateClientConfig): ApiClient => {
  const resolveToken = (): string =>
    config.authenticityToken || config.resolveToken();

  const request = async <T>(
    method: string,
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> => {
    const url = buildUrl(path, options?.params);
    const isFormData = body instanceof FormData;

    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...options?.headers,
    };

    if (!isFormData && !options?.skipContentType) {
      headers['Content-Type'] = 'application/json';
    }

    let processedBody: BodyInit | undefined;

    if (body !== undefined) {
      if (isFormData) {
        if (config.injectToken) {
          body.append('authenticity_token', resolveToken());
        }
        processedBody = body;
      } else {
        const jsonBody = config.injectToken
          ? { authenticity_token: resolveToken(), ...(body as object) }
          : body;
        processedBody = JSON.stringify(jsonBody);
      }
    } else if (config.injectToken && method !== 'GET') {
      processedBody = JSON.stringify({ authenticity_token: resolveToken() });
    }

    const response = await fetch(url, {
      method,
      headers,
      body: processedBody,
      signal: options?.signal,
    });

    if (!response.ok) {
      return handleErrorResponse(response);
    }

    return parseResponse<T>(response);
  };

  return {
    get: <T>(path: string, options?: RequestOptions) =>
      request<T>('GET', path, undefined, options),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>('POST', path, body, options),
    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>('PUT', path, body, options),
    patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>('PATCH', path, body, options),
    delete: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>('DELETE', path, body, options),
  };
};

// --- Public API ---

interface CreateApiClientParams {
  authenticityToken?: string;
}

export const createApiClient = ({ authenticityToken }: CreateApiClientParams): ApiClient =>
  buildClient({
    authenticityToken,
    resolveToken: getAuthenticityToken,
    injectToken: true,
  });

export const createPublicApiClient = (): ApiClient =>
  buildClient({
    resolveToken: () => '',
    injectToken: false,
  });
